/* Shared prev/next carousel controller.
   Manages every [data-actevna-carousel] on the page: shows arrows only when the row overflows,
   scrolls one item per click, disables each arrow at its scroll end, honours reduced-motion.
   Generic hooks so any section can consume it (row/prev/next via data-attributes). */

(function () {
  const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behaviour = () => (prefersReduced() ? 'instant' : 'smooth');

  function initCarousel(carousel) {
    /* Init once per element (guards editor re-render + duplicate module tags). */
    if (carousel.dataset.actevnaCarouselReady) return;

    const row = carousel.querySelector('[data-actevna-carousel-row]');
    const prevBtn = carousel.querySelector('[data-actevna-carousel-prev]');
    const nextBtn = carousel.querySelector('[data-actevna-carousel-next]');
    if (!row || !prevBtn || !nextBtn) return;

    carousel.dataset.actevnaCarouselReady = 'true';

    /* One card + one gap = the scroll step (measured, not hardcoded, so it tracks the layout). */
    function scrollStep() {
      const first = row.firstElementChild;
      if (!first) return row.clientWidth * 0.8;
      const styles = getComputedStyle(row);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    /* Arrows only matter when the row overflows; in grid mode scrollWidth ~= clientWidth (2px tolerance). */
    function hasOverflow() {
      return row.scrollWidth > row.clientWidth + 2;
    }

    /* Recompute overflow + scroll position, then update: the arrows (desktop) and the edge fades.
       The fades are a scroll affordance and run regardless of the arrows (they matter on mobile too):
       a side fades only when there is hidden content that way. */
    function update() {
      const overflow = hasOverflow();
      const atStart = row.scrollLeft <= 1;
      const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;

      carousel.toggleAttribute('data-overflow', overflow);
      /* Fade the far edge only while content is hidden that way (none when it all fits). */
      carousel.toggleAttribute('data-fade-start', overflow && !atStart);
      carousel.toggleAttribute('data-fade-end', overflow && !atEnd);

      if (!overflow) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    prevBtn.addEventListener('click', () => {
      row.scrollBy({ left: -scrollStep(), behavior: behaviour() });
    });
    nextBtn.addEventListener('click', () => {
      row.scrollBy({ left: scrollStep(), behavior: behaviour() });
    });

    /* Update arrow state on scroll (rAF-debounced to avoid thrash). */
    let rafId = null;
    row.addEventListener(
      'scroll',
      () => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(update);
      },
      { passive: true }
    );

    /* Re-check overflow when the row resizes (window resize, editor panel, font load). */
    new ResizeObserver(update).observe(row);

    update();
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-actevna-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* Theme editor: init carousels inside a freshly-loaded section. */
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
